/**
 * Concept Dispatch — main (office) layout on the Concept DESIGN STANDARD shell (2026-09-04).
 * Header: navy bar, Crimson mark + "Dispatch", search, Apps (ecosystem launcher), Tech Mail (shared tech@ mailbox with
 * unread badge), notifications, user, Sign out. Sidebar: standard navy menu, active item = red accent. Mobile: bottom nav.
 * Footer: company info from admin.concept.bz → Company info (per-app section "dispatch").
 * Logic (notifications, push, auth, routing) is unchanged from the previous MainLayout.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import api, { techMailApi } from '../../services/api';
import {
  AppBar, Avatar, Badge, BottomNavigation, BottomNavigationAction, Box, Button, Chip, Drawer, IconButton, InputBase,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, SwipeableDrawer, Toolbar, Tooltip,
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu as MenuIcon, Schedule as ScheduleNavIcon, ChevronLeft as ChevronLeftIcon, Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon, Map as MapIcon, Assignment as TicketsIcon, Inventory as InventoryIcon,
  AdminPanelSettings as AdminIcon, Notifications as NotificationsIcon, NotificationsOff as NotificationsOffIcon,
  NotificationsActive as NotificationsActiveIcon, DoneAll as DoneAllIcon, Search as SearchIcon, Settings as SettingsIcon,
  Logout as LogoutIcon, Close as CloseIcon, People as PeopleIcon, PhoneAndroid as DevicesNavIcon, PunchClock as TimeClockIcon,
  Gavel as GavelIcon, MarkEmailUnread as TechInboxIcon, Mail as TechMailIcon, Apps as AppsIcon,
  ShoppingCart as InternetPurchasesIcon,
} from '@mui/icons-material';
import BreakComplianceBanner from '../BreakComplianceBanner';
import ReviewFlagsBanner from '../ReviewFlagsBanner';
import ClosedMailQueue from '../ClosedMailQueue';
import { CC, GRAD } from '../../theme/theme';
import { useWorkspace } from '@/contexts/WorkspaceContext';   // multi-role (2026-09-04)
import RolePickerDialog, { RoleChip } from '@/pages/tech/RolePicker';

const drawerWidth = 232;
const mobileDrawerWidth = 280;
const MARK = 'https://concept.bz/ui/logo/concept-mark.svg';
const LOGO = 'https://concept.bz/ui/logo/concept-logo.svg';
const APPS_URL = 'https://admin.concept.bz/apps';

interface MenuItemType { text: string; icon: JSX.Element; path: string; badgeKey?: 'tickets' | 'inbox' }

const menuItems: MenuItemType[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
  { text: 'Schedule', icon: <ScheduleNavIcon />, path: '/schedule' },
  { text: 'Map', icon: <MapIcon />, path: '/map' },
  { text: 'Tickets', icon: <TicketsIcon />, path: '/tickets', badgeKey: 'tickets' },
  { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
  { text: 'Team', icon: <PeopleIcon />, path: '/team' },
  { text: 'Devices', icon: <DevicesNavIcon />, path: '/devices' },
  { text: 'Time Clock', icon: <TimeClockIcon />, path: '/admin?tab=timeclock' },
  { text: 'Compliance', icon: <GavelIcon />, path: '/compliance' },
  { text: 'Tech Mail', icon: <TechMailIcon />, path: 'tech-mail' },   // shared tech@customer.concept.bz mailbox, opens webmail signed in
  { text: 'Tech Inbox', icon: <TechInboxIcon />, path: '/tech-inbox', badgeKey: 'inbox' },
  { text: 'Internet purchases', icon: <InternetPurchasesIcon />, path: '/internet-purchases' },   // online software payments (2026-10-17)
  { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
];

// bottom bar (phones): Devices stays first-class (2026-07-13); Tech Mail replaces Calendar (Schedule covers it on a phone)
const bottomNavItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Schedule', icon: <ScheduleNavIcon />, path: '/schedule' },
  { text: 'Tickets', icon: <TicketsIcon />, path: '/tickets' },
  { text: 'Tech Mail', icon: <TechMailIcon />, path: 'tech-mail' },
  { text: 'More', icon: <MenuIcon />, path: 'more' },
];

type CompanyInfo = { label?: string; phone?: string; email?: string; hours?: string; hoursWeekend?: string; website?: string;
  physical?: { street?: string; city?: string; state?: string; zip?: string }; note?: string };

/** opens the shared tech@ mailbox in a new tab, already signed in (5-minute signed link from the API) */
function openTechMail(mailbox?: string) {
  const w = window.open('', '_blank');
  techMailApi.link(mailbox).then((r: any) => { if (w) w.location.href = r.data.url; else window.open(r.data.url, '_blank'); })
    .catch(() => { if (w) w.close(); alert('Tech Mail is not available right now.'); });
}

export default function MainLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { notifications: notifItems, unreadCount, markRead, markAllRead } = useNotifications();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush, permission: pushPermission } = usePushNotifications();
  useEffect(() => { if (pushSupported && !pushSubscribed && pushPermission === 'granted') subscribePush(); }, [pushSupported, pushSubscribed, pushPermission, subscribePush]);

  const [open, setOpen] = useState(!isSmallScreen);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [mailUnseen, setMailUnseen] = useState(0);
  const [inboxUnmatched, setInboxUnmatched] = useState(0);
  const [openTickets, setOpenTickets] = useState<number | null>(null);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout: ssoLogout, user } = useAuth() as any;
  const { ws, activeRoleName } = useWorkspace();
  const [mailAnchor, setMailAnchor] = useState<null | HTMLElement>(null);
  const [rolePick, setRolePick] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]);
  useEffect(() => { if (ws?.needs_role_choice) setRolePick(true); }, [ws?.needs_role_choice]);
  // shared mailboxes the user's roles may open (2026-09-04)
  useEffect(() => {
    let live = true;
    const tick = () => api.get('/tech-mail/accounts').then((r: any) => live && setAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    tick(); const h = setInterval(tick, 60000);
    return () => { live = false; clearInterval(h); };
  }, [ws?.active_role?.id]);
  const mailClick = (e: React.MouseEvent<HTMLElement>) => { if (accounts.length <= 1) openTechMail(accounts[0]?.address); else setMailAnchor(e.currentTarget); };
  // menu gated by authorization: only functions the user's roles allow (SuperAdmin = all); unknown until loaded = show all
  const allowedDesktop = ws ? new Set(ws.functions.map((f) => f.desktop)) : null;
  const visibleMenu = allowedDesktop ? menuItems.filter((m) => m.path === 'tech-mail' || allowedDesktop.has(m.path)) : menuItems;

  useEffect(() => { setOpen(!isSmallScreen); }, [isSmallScreen]);

  // badges: shared mailbox unread, Tech Inbox unmatched, open tickets — refreshed every 60 s
  useEffect(() => {
    let live = true;
    const tick = () => {
      api.get('/tech-mail/accounts').then((r: any) => live && setMailUnseen((Array.isArray(r.data) ? r.data : []).reduce((n: number, a: any) => n + (a.unseen || 0), 0))).catch(() => {});
      api.get('/tech-inbox', { params: { status: 'unmatched', page_size: 1 } }).then((r: any) => live && setInboxUnmatched(r.data?.unmatched || 0)).catch(() => {});
      api.get('/tickets', { params: { status: 'new,assigned,scheduled,en_route,on_site,in_progress,pending_parts', page_size: 1 } })
        .then((r: any) => live && setOpenTickets(typeof r.data?.total === 'number' ? r.data.total : null)).catch(() => {});
    };
    tick(); const h = setInterval(tick, 60000);
    return () => { live = false; clearInterval(h); };
  }, []);
  // footer: company info from admin.concept.bz (design standard rule 1)
  useEffect(() => {
    fetch('https://concept.bz/api/cms/company-info/for/dispatch').then(r => r.ok ? r.json() : null).then(j => j && setCompany(j)).catch(() => {});
  }, []);

  const handleDrawerToggle = () => { if (isMobile) setMobileOpen(!mobileOpen); else setOpen(!open); };
  const handleMobileDrawerClose = () => setMobileOpen(false);
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotifOpen = (e: React.MouseEvent<HTMLElement>) => setNotifAnchorEl(e.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);
  const handleLogout = async () => { handleMenuClose(); try { await ssoLogout(); } catch (_) {} navigate('/login', { replace: true }); };

  const handleNavigation = (path: string) => {
    if (path === 'more') { setMobileOpen(true); return; }
    if (path === 'tech-mail') { if (accounts.length > 1) setMailAnchor(document.body as any); else openTechMail(accounts[0]?.address); if (isMobile) setMobileOpen(false); return; }
    navigate(path); if (isMobile) setMobileOpen(false);
  };
  const getCurrentNavValue = () => { const i = bottomNavItems.findIndex(item => item.path === location.pathname); return i >= 0 ? i : 4; };
  const badgeFor = (k?: 'tickets' | 'inbox') => (k === 'tickets' ? openTickets : k === 'inbox' ? inboxUnmatched : null);

  const displayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : '';
  const initials = user ? `${(user.first_name || user.email || '?')[0]}${(user.last_name || '')[0] || ''}`.toUpperCase() : '';
  const roleName = activeRoleName || (user?.roles?.length ? (typeof user.roles[0] === 'string' ? user.roles[0] : user.roles[0]?.name) : '');

  const drawerContent = (
    <>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 56 } }} />
      <Box sx={{ overflow: 'auto', mt: 1, px: 0.5, flex: 1 }}>
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1 }}>
            <IconButton onClick={handleMobileDrawerClose} sx={{ color: 'rgba(255,255,255,0.7)' }}><CloseIcon /></IconButton>
          </Box>
        )}
        <List sx={{ py: 0 }}>
          {visibleMenu.map((item) => {
            const isActive = item.path.includes('?')
              ? (location.pathname + location.search) === item.path
              : (location.pathname === item.path && !location.search);
            const badge = badgeFor(item.badgeKey);
            const showLabel = open || isMobile;
            return (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <Tooltip title={showLabel ? '' : item.text} placement="right">
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      minHeight: { xs: 48, sm: 42 }, justifyContent: showLabel ? 'initial' : 'center', px: 1.5, py: 0.75, borderRadius: 2, m: '2px 8px',
                      background: isActive ? GRAD.accent : 'transparent',
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.85)',
                      boxShadow: isActive ? '0 2px 4px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.35)' : 'none',
                      '&:hover': { background: isActive ? GRAD.accent : 'rgba(255,255,255,0.08)', color: '#fff' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: showLabel ? 1.5 : 'auto', justifyContent: 'center', color: 'inherit', '& svg': { fontSize: { xs: '1.4rem', sm: '1.2rem' } } }}>
                      {!showLabel && badge ? <Badge badgeContent={badge} color="secondary">{item.icon}</Badge> : item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} sx={{ opacity: showLabel ? 1 : 0, transition: 'opacity .2s', my: 0 }}
                      primaryTypographyProps={{ fontWeight: isActive ? 700 : 500, fontSize: { xs: '1rem', sm: '0.9rem' } }} />
                    {showLabel && !!badge && (
                      <Chip label={badge} size="small" sx={{ height: 18, fontSize: '0.6875rem', fontWeight: 700, color: '#fff',
                        background: isActive ? 'rgba(0,0,0,.25)' : (item.badgeKey === 'inbox' ? CC.red : 'rgba(255,255,255,.18)'), '& .MuiChip-label': { px: 0.9 } }} />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Box>
      {(open || isMobile) && (
        <Box sx={{ mt: 'auto', px: 2, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', fontSize: 11, lineHeight: 1.5 }}>
          Concept Dispatch<br />Design standard 2026-09
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1, background: CC.navy }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 56 }, px: { xs: 1, sm: 1.75 }, gap: 1 }}>
          <IconButton onClick={handleDrawerToggle} edge="start" sx={{ color: '#fff', mr: 0.5 }} aria-label="Menu">
            {!isMobile && open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Box component="a" href="/dashboard" onClick={(e: any) => { e.preventDefault(); navigate('/dashboard'); }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: '#fff', textDecoration: 'none', minWidth: 0 }}>
            <Box component="img" src={MARK} alt="" sx={{ width: 30, height: 30, filter: 'drop-shadow(1px 1px 0 rgba(0,0,0,.45))' }} />
            <Typography sx={{ font: `600 15px ${CC.fontBody}`, whiteSpace: 'nowrap' }}>Dispatch</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.7, display: { xs: 'none', md: 'block' }, whiteSpace: 'nowrap' }}>Concept Communications</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', borderRadius: 1.5, height: 34, px: 1.25, width: 280, mr: 0.5 }}>
            <SearchIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,.75)' }} />
            <InputBase placeholder="Search tickets, customers, techs…" sx={{ flex: 1, color: '#fff', '& input': { fontSize: '0.8125rem', '&::placeholder': { color: 'rgba(255,255,255,.7)', opacity: 1 } } }} />
          </Box>
          <Tooltip title="All Concept apps"><Button component="a" href={APPS_URL} target="_blank" rel="noreferrer" startIcon={<AppsIcon />}
            sx={{ color: '#fff', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.16)', minHeight: 34, px: 1.25, display: { xs: 'none', sm: 'inline-flex' } }}>Apps</Button></Tooltip>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 0.5 }}><RoleChip /></Box>
          <Tooltip title={accounts.length > 1 ? 'Mail — choose a mailbox' : `Tech Mail — ${accounts[0]?.address || 'tech@customer.concept.bz'}${mailUnseen ? ` (${mailUnseen} new)` : ''}`}>
            <IconButton onClick={mailClick} sx={{ color: '#fff', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.16)', width: 38, height: 34, borderRadius: 1.5 }}>
              <Badge badgeContent={mailUnseen} color="secondary" invisible={!mailUnseen} sx={{ '& .MuiBadge-badge': { boxShadow: `0 0 0 2px ${CC.navy}`, fontSize: 10, height: 16, minWidth: 16 } }}><TechMailIcon sx={{ fontSize: 20 }} /></Badge>
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleNotifOpen} sx={{ color: '#fff', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.16)', width: 38, height: 34, borderRadius: 1.5 }}>
            <Badge badgeContent={unreadCount} color="secondary" invisible={unreadCount === 0} sx={{ '& .MuiBadge-badge': { boxShadow: `0 0 0 2px ${CC.navy}`, fontSize: 10, height: 16, minWidth: 16 } }}><NotificationsIcon sx={{ fontSize: 20 }} /></Badge>
          </IconButton>
          <Box onClick={handleMenuOpen} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', ml: 0.5, pr: 0.5 }}>
            <Avatar sx={{ width: 26, height: 26, fontSize: 11, background: GRAD.accent, boxShadow: '0 1px 0 rgba(0,0,0,.4)' }}>{initials || '•'}</Avatar>
            <Typography sx={{ fontSize: 13, color: '#fff', display: { xs: 'none', sm: 'block' }, maxWidth: 140 }} noWrap>{displayName}</Typography>
          </Box>
          <Button onClick={handleLogout} sx={{ color: '#fff', background: 'rgba(255,255,255,.10)', border: '1px solid rgba(255,255,255,.16)', minHeight: 34, px: 1.25, display: { xs: 'none', sm: 'inline-flex' } }}>Sign out</Button>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { mt: 1, minWidth: 220 } }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${CC.line}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: CC.navy }}>{displayName || 'Signed in'}</Typography>
              <Typography variant="caption" color="text.secondary">{roleName || user?.email}</Typography>
            </Box>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/admin'); }}><ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>Settings</MenuItem>
            {(ws?.roles?.length || 0) > 1 && <MenuItem onClick={() => { handleMenuClose(); setRolePick(true); }}><ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>Switch role ({activeRoleName || 'choose'})</MenuItem>}
            {accounts.length === 0 && <MenuItem onClick={() => { handleMenuClose(); openTechMail(); }}><ListItemIcon><TechMailIcon fontSize="small" /></ListItemIcon>Tech Mail</MenuItem>}
            {accounts.map((a: any) => <MenuItem key={a.address} onClick={() => { handleMenuClose(); openTechMail(a.address); }}><ListItemIcon><TechMailIcon fontSize="small" /></ListItemIcon>{a.label}{a.unseen ? ` (${a.unseen})` : ''}</MenuItem>)}
            <MenuItem onClick={handleLogout} sx={{ color: CC.red }}><ListItemIcon><LogoutIcon fontSize="small" sx={{ color: CC.red }} /></ListItemIcon>Sign out</MenuItem>
          </Menu>

          <Menu anchorEl={mailAnchor} open={Boolean(mailAnchor)} onClose={() => setMailAnchor(null)} PaperProps={{ sx: { mt: 1, minWidth: 250 } }}>
            {accounts.map((a: any) => (
              <MenuItem key={a.address} onClick={() => { setMailAnchor(null); openTechMail(a.address); }}>
                <ListItemIcon><Badge badgeContent={a.unseen || 0} color="secondary" invisible={!a.unseen}><TechMailIcon fontSize="small" /></Badge></ListItemIcon>
                <Box><Typography variant="body2" fontWeight={700}>{a.label}</Typography><Typography variant="caption" color="text.secondary">{a.address}</Typography></Box>
              </MenuItem>
            ))}
          </Menu>
          <RolePickerDialog open={rolePick} forced={!!ws?.needs_role_choice} onClose={() => setRolePick(false)} />

          <Menu anchorEl={notifAnchorEl} open={Boolean(notifAnchorEl)} onClose={handleNotifClose} PaperProps={{ sx: { mt: 1, width: { xs: 300, sm: 340 }, maxWidth: '92vw' } }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${CC.line}` }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: CC.navy }}>Notifications</Typography></Box>
            {notifItems.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><NotificationsOffIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} /><Typography variant="body2" color="text.secondary">No notifications yet</Typography></Box>
            ) : (
              notifItems.slice(0, 8).map((n: any) => (
                <MenuItem key={n.id} onClick={() => { markRead(n.id); if (n.url) navigate(n.url); handleNotifClose(); }}
                  sx={{ whiteSpace: 'normal', borderLeft: n.is_read ? '3px solid transparent' : `3px solid ${CC.red}`, py: 1.25 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={n.is_read ? 400 : 700} noWrap>{n.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>{n.body}</Typography>
                    <Typography variant="caption" color="text.disabled">{new Date(n.created_at).toLocaleString()}</Typography>
                  </Box>
                </MenuItem>
              ))
            )}
            {notifItems.length > 0 && (
              <Box sx={{ p: 1, borderTop: `1px solid ${CC.line}`, display: 'flex', justifyContent: 'space-between' }}>
                <Button size="small" startIcon={<DoneAllIcon />} onClick={() => { markAllRead(); }}>Mark all read</Button>
                {pushSupported && !pushSubscribed && <Button size="small" startIcon={<NotificationsActiveIcon />} onClick={() => subscribePush()}>Enable push</Button>}
              </Box>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      {isMobile && (
        <SwipeableDrawer anchor="left" open={mobileOpen} onClose={handleMobileDrawerClose} onOpen={() => setMobileOpen(true)} disableSwipeToOpen={false} swipeAreaWidth={20}
          ModalProps={{ keepMounted: true }} PaperProps={{ sx: { width: mobileDrawerWidth, background: GRAD.sidebar, borderRight: 'none' } }}>
          {drawerContent}
        </SwipeableDrawer>
      )}
      {!isMobile && (
        <Drawer variant="permanent" open={open}
          sx={{ width: open ? drawerWidth : 64, flexShrink: 0, '& .MuiDrawer-paper': { width: open ? drawerWidth : 64, boxSizing: 'border-box', background: GRAD.sidebar,
            transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }), overflowX: 'hidden', borderRight: 'none' } }}>
          {drawerContent}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, width: '100%', minWidth: 0, minHeight: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 56 } }} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 }, pb: { xs: 10, sm: 3 }, flex: 1 }}>
          <BreakComplianceBanner />
          <ReviewFlagsBanner />
          <ClosedMailQueue />
          <Outlet />
        </Box>
        {/* footer — company info from admin (hidden on phones, where the bottom nav lives) */}
        {!isMobile && (
          <Box component="footer" sx={{ background: CC.navy, color: 'rgba(255,255,255,.85)', fontSize: 13, mt: 2 }}>
            <Box sx={{ maxWidth: 1180, mx: 'auto', px: 2, pt: 2.5, pb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1.2fr 1fr 1fr 1.2fr' }, gap: 2 }}>
              <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Box component="img" src={LOGO} alt="Concept Communications" sx={{ height: 52, display: 'block', mb: 0.75 }} />
                <Typography sx={{ fontSize: 12, opacity: 0.8 }}>Managed IT · VoIP · Digital signage · HME drive-thru</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', opacity: 0.7 }}>Contact</Typography>
                <span>{company?.phone || '(916) 652-2399'}</span>
                <Box component="a" href={`mailto:${company?.email || 'support@concept.bz'}`} sx={{ color: '#fff', textDecoration: 'none' }}>{company?.email || 'support@concept.bz'}</Box>
                <Box component="a" href="https://support.concept.bz/" target="_blank" rel="noreferrer" sx={{ color: '#fff', textDecoration: 'none' }}>support.concept.bz</Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', opacity: 0.7 }}>Hours</Typography>
                <span>{company?.hours || 'Mon–Fri 8:00–4:30'}</span>
                <span>Weekend: {company?.hoursWeekend || 'Closed'}</span>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', opacity: 0.7 }}>Office</Typography>
                <span>{company?.label || 'Concept Communications'}</span>
                {company?.physical?.street && <span>{company.physical.street}</span>}
                <span>{company?.physical ? `${company.physical.city || ''}${company.physical.city ? ', ' : ''}${company.physical.state || ''} ${company.physical.zip || ''}` : 'Auburn, CA'}</span>
              </Box>
              <Box sx={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,.12)', pt: 1, fontSize: 12, opacity: 0.7 }}>
                © {new Date().getFullYear()} Concept Communications · Dispatch
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: theme.zIndex.drawer + 2, borderRadius: 0, background: CC.navy, pb: 'env(safe-area-inset-bottom)' }} elevation={3}>
          <BottomNavigation value={getCurrentNavValue()} onChange={(_, v) => handleNavigation(bottomNavItems[v].path)} showLabels sx={{ height: 64, background: CC.navy, px: 0.5 }}>
            {bottomNavItems.map((item) => (
              <BottomNavigationAction key={item.text} label={item.text}
                icon={item.path === '/tickets' && openTickets ? <Badge badgeContent={openTickets} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15 } }}>{item.icon}</Badge>
                  : item.path === 'tech-mail' && mailUnseen ? <Badge badgeContent={mailUnseen} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 15, height: 15 } }}>{item.icon}</Badge>
                  : item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
